import React, { useMemo, useState } from "react";
import { Card, List, Avatar, Tag, Typography, Divider, Empty, Input } from "antd";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";

const { Text } = Typography;

export default function ClockInOutPanel({ checkins = [] }) {

  const [searchText, setSearchText] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const { checkedInEmployees, lateEmployees } = useMemo(() => {

    const list = Array.isArray(checkins) ? checkins : [];

    let checkedInEmployees = [];
    let lateEmployees = [];

    list.forEach(emp => {
      if (emp.check_in) checkedInEmployees.push(emp);

      if (emp.late_minutes > 0) {
        lateEmployees.push({
          name: emp.employee_name,
          minutes: emp.late_minutes
        });
      }
    });

    if (searchText) {
      const searchLower = searchText.toLowerCase();

      checkedInEmployees = checkedInEmployees.filter(emp =>
        emp.employee_name?.toLowerCase().includes(searchLower)
      );

      lateEmployees = lateEmployees.filter(emp =>
        emp.name?.toLowerCase().includes(searchLower)
      );
    }

    return {
      checkedInEmployees,
      lateEmployees
    };

  }, [checkins, searchText]);

  return (
    <Card
      title={`Clock In / Out (${checkedInEmployees.length})`}
      extra={
        showSearch ? (
          <CloseOutlined
            style={{ cursor: "pointer" }}
            onClick={() => {
              setShowSearch(false);
              setSearchText("");
            }}
          />
        ) : (
          <SearchOutlined
            style={{ cursor: "pointer" }}
            onClick={() => setShowSearch(true)}
          />
        )
      }
       styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          },
       }}
    >
      
      {showSearch && (
        <div style={{ marginBottom: 12 }}>
          <Input
            autoFocus
            placeholder="Search employee..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      )}

      {checkedInEmployees.length === 0 ? (
        <Empty description="No check-ins today" />
      ) : (
        // Capped to roughly 5-6 rows' worth of height with the rest
        // scrollable — previously unbounded, so this card grew to fit
        // every checked-in employee and stretched taller than its row
        // siblings (Employee Status / Attendance Overview), leaving visible
        // empty space under them.
        <div style={{ maxHeight: 420, overflowY: "auto" }}>
          <List
            dataSource={checkedInEmployees}
            renderItem={(emp) => (
              <List.Item>

                <List.Item.Meta
                  avatar={
                    <Avatar src={emp?.employee_photo_path}>
                      {!emp?.employee_photo_path && emp.employee_name?.[0]}
                    </Avatar>
                  }
                  title={emp.employee_name}
                  description={
                    <div>
                      <div>
                        <Text type="secondary">Clock In</Text> : {emp.check_in?.slice(0, 5)}
                      </div>

                      {emp.check_out && (
                        <div>
                          <Text type="secondary">Clock Out</Text> : {emp.check_out.slice(0, 5)}
                        </div>
                      )}
                    </div>
                  }
                />

                {emp.check_out ? (
                  <Tag color="blue">Out</Tag>
                ) : (
                  <Tag color="green">In</Tag>
                )}

              </List.Item>
            )}
          />
        </div>
      )}

      {lateEmployees.length > 0 && (
        <>
          <Divider orientation="left">Late Arrivals</Divider>

          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            <List
              dataSource={lateEmployees}
              renderItem={(emp) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar>{emp.name?.[0]}</Avatar>}
                    title={emp.name}
                  />
                  <Tag color="red">{emp.minutes} min late</Tag>
                </List.Item>
              )}
            />
          </div>
        </>
      )}

    </Card>
  );
}